<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import { Head } from '@inertiajs/vue3';
import { Bell, CreditCard, Eye, FileText, History, Lock, Shield, Smartphone } from 'lucide-vue-next';
interface Feature {
    icon: any;
    title: string;
    description: string;
}

const features: Feature[] = [
    {
        icon: Smartphone,
        title: 'Mobile app or web interface',
        description:
            'Project digital safe includes a mobile app or web interface that allows its users to manage the contents of the safe from any location and device.',
    },
    {
        icon: FileText,
        title: 'Registration of items',
        description:
            'Users can register banknotes (and later items) that they wish to store in the digital safe by scanning them using a mobile phone camera or a special reader.',
    },
    {
        icon: Shield,
        title: 'Transforming into digital form',
        description:
            'Registered banknotes(items) are converted into digital form and stored in a secure database of the digital safe system. Each item receives a unique digital ID that is associated with its data.',
    },
    {
        icon: Lock,
        title: 'Security and authorization',
        description:
            'Access to the digital safe is protected using advanced security mechanisms, such as two-factor authorization, data encryption and the use of biometric user data. By using blockchain technology along with the efficient and secure X1 network, the process of securing banknotes becomes more effective and reliable, ensuring transparency, authenticity and security.',
    },
    {
        icon: Eye,
        title: 'Monitoring the contents',
        description:
            'Users can monitor the contents of their digital safe via mobile app or web interface. They can check what banknotes (items) are stored in the safe.',
    },
    {
        icon: Bell,
        title: 'Notification of unauthorized access attempts',
        description:
            'The digital safe system can be equipped with monitoring and alerting features that notify users of suspicious activity, unauthorized access attempts, or other potential threats.',
    },
    {
        icon: History,
        title: 'Transaction history tracking',
        description:
            'Each transaction related to the items stored in the digital safe, such as their deposit, withdrawal or transfer to another user, is tracked and recorded in the system. Thanks to this, users can monitor the flow of their values.',
    },
    {
        icon: CreditCard,
        title: 'Integration with other financial services',
        description:
            'The digital safe can be integrated with other financial services, such as mobile payment or online banking platforms. Thanks to this, users can easily manage their finances and conduct various types of transactions.',
    },
];
</script>

<template>
    <Head title="About the project">
        <meta
            head-key="description"
            name="description"
            content="An innovative solution aimed at safely storing valuable items, such as banknotes, documents or other costly items, in a secure way that is easily accessible to the owner."
        />
        <meta
            name="keywords"
            content="Digital Safe, Project Digital Safe, Main Features & Design Elements, Safe & Convenient Storage, Mobile app or web interface, Registration of items, Transforming into digital form, Security and authorization, Monitoring the contents, Notification of unauthorized access attempts, Transaction history tracking, Integration with other financial services"
        />
    </Head>
    <div class="min-h-screen bg-background">
        <Header />

        <main class="pt-20">
            <!-- Hero Section -->
            <section class="relative overflow-hidden py-20">
                <div class="bg-gradient-subtle absolute inset-0 opacity-50"></div>
                <div class="relative z-10 container mx-auto px-4">
                    <div class="mx-auto max-w-4xl text-center">
                        <h1 class="mb-6 text-4xl font-bold md:text-6xl">
                            Project <span class="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Digital Safe</span>
                        </h1>
                        <p class="text-lg leading-relaxed text-muted-foreground md:text-xl">
                            An innovative solution aimed at safely storing valuable items, such as banknotes, documents or other costly items, in a
                            secure way that is easily accessible to the owner.
                        </p>
                    </div>
                </div>
            </section>

            <!-- Features Section -->
            <section class="py-20">
                <div class="container mx-auto px-4">
                    <div class="mb-16 text-center">
                        <h2 class="mb-4 text-3xl font-bold md:text-4xl">Main Features & <span class="text-primary">Design Elements</span></h2>
                        <p class="text-lg text-muted-foreground">Listed below are the main features and design elements of project digital safe</p>
                    </div>

                    <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div
                            v-for="(feature, index) in features"
                            :key="index"
                            class="glass-card hover:glow glow-hover p-8 transition-all duration-300"
                        >
                            <div class="flex items-start space-x-4">
                                <div class="glow flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-hero">
                                    <component :is="feature.icon" class="h-6 w-6 text-primary-foreground" />
                                </div>
                                <div>
                                    <h3 class="mb-3 text-xl font-semibold text-foreground">{{ index + 1 }}. {{ feature.title }}</h3>
                                    <p class="leading-relaxed text-muted-foreground">
                                        {{ feature.description }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Conclusion Section -->
            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto px-4">
                    <div class="mx-auto max-w-4xl text-center">
                        <h2 class="mb-8 text-3xl font-bold md:text-4xl">Safe & <span class="text-primary">Convenient Storage</span></h2>
                        <p class="mb-8 text-lg leading-relaxed text-muted-foreground">
                            Project digital safe provides safe and convenient storage of valuables, allowing users easy access and control over them.
                            Thanks to the use of advanced security technologies and integration with other financial services, this project is an
                            innovative solution for people seeking a safe and effective way to manage their values.
                        </p>
                        <div class="flex flex-wrap justify-center gap-4">
                            <div class="glass-card px-6 py-3">
                                <span class="font-semibold text-primary">Advanced Security</span>
                            </div>
                            <div class="glass-card px-6 py-3">
                                <span class="font-semibold text-primary">Blockchain Technology</span>
                            </div>
                            <div class="glass-card px-6 py-3">
                                <span class="font-semibold text-primary">X1 Network</span>
                            </div>
                            <div class="glass-card px-6 py-3">
                                <span class="font-semibold text-primary">Financial Integration</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>

        <Footer />
    </div>
</template>
